/** SIMBOLURILE DE ELECTRICE și traseele lor, în convenția de planșă.
 *
 *  SIMBOLURILE SUNT CELE ALE BIROULUI (foaia arhitectului, rândurile 2, 3 și 4),
 *  pentru prizele, întrerupătoarele și corpurile de iluminat pe care le desenează
 *  ea, și cele din NORMĂ (IEC 60617) pentru restul: tabloul e dreptunghiul
 *  hașurat. Nu e purism — un electrician
 *  citește planșa asta, iar un simbol inventat îl obligă să caute legenda la
 *  fiecare aparat.
 *
 *  DESENATE ÎN METRI, ca tot restul pânzei, dar cu GROSIMEA LINIEI în pixeli
 *  constanți (`vectorEffect="non-scaling-stroke"`): la scara la care se citește
 *  un tronson întreg, o linie scrisă în metri ar fi fost mai groasă decât priza.
 *
 *  TRASEELE SE DESENEAZĂ PESTE TOT, SIMBOLURILE DEASUPRA LOR. Un fir care trece
 *  peste o priză se citește ca și cum ar intra în ea, ceea ce de multe ori nici
 *  nu e adevărat — de-aia aparatul acoperă firul, nu invers.
 */
import type { ReactNode } from "react";
import type { Electric, Pt } from "../doc";
import { aparatul, CIRCUITE, ePePerete, familiaDozei, familiaInDoza, familiileDozei,
         marimeaSimbolului, ramaDozei,
         type Circuit, type FamilieDoza, type Traseu } from "./electric";

export const LINIE = "#1b1b1b";
const FOND = "#ffffff";
/** The architect's inks: sockets red, switches and lights blue, picked off
 *  their mock-up and kept apart from the circuit palette the wires use. */
const SOCKET_INK = "#d81e1e";
const SWITCH_INK = "#1f3fd6";

/** Grosimile, în pixeli — aceeași socoteală ca la mobilier. */
const K = 55;
const g = (m: number) => Math.max(0.7, m * K);
/** The line of the architect's symbols: 1.1 px reads too thin on the plan,
 *  so twice that. The filled kinds keep theirs. */
const LINIA_FOII = 2 * g(0.02);

const S = (ink: string) =>
  ({ stroke: ink, fill: "none", vectorEffect: "non-scaling-stroke" as const });

/** PRIZA: semicercul plin cu bara prin diametru. Bara arată peretele; discul,
 *  partea dinspre cameră. Dubla poartă două bare. */
function Priza({ d, n = 1, ink }: {
  d: number; n?: number; ink: string;
}) {
  const r = d / 2;
  return (
    <g>
      <path d={`M${-r} 0A${r} ${r} 0 0 1 ${r} 0`} {...S(ink)}
            fill={ink} strokeWidth={g(0.02)} />
      <line x1={-r} y1={0} x2={r} y2={0} {...S(ink)} strokeWidth={g(0.028)} />
      {/* contactul de protecție: liniuța din mijloc. O priză fără ea e o priză
          fără împământare, iar într-o locuință nouă așa ceva nu se pune. */}
      <line x1={0} y1={0} x2={0} y2={-r * 0.62} {...S(ink)} strokeWidth={g(0.02)}
            stroke={FOND} />
      {n > 1 && <line x1={-r * 0.5} y1={r * 0.22} x2={r * 0.5} y2={r * 0.22}
                      {...S(ink)} strokeWidth={g(0.022)} />}
    </g>
  );
}

/** ÎNTRERUPĂTORUL DUBLU: cercul mic cu două pârghii în sus, la 45°. */
function Comanda({ d, ink }: { d: number; ink: string }) {
  const r = d * 0.24;
  const L = d * 0.72;
  const bara = (dx: number) => (
    <line x1={dx} y1={0} x2={dx + L * 0.62} y2={-L * 0.62} {...S(ink)}
          strokeWidth={g(0.024)} />
  );
  return (
    <g>
      <circle cx={0} cy={0} r={r} {...S(ink)} fill={ink} />
      {bara(0)}{bara(d * 0.26)}
    </g>
  );
}

/** THE ARCHITECT'S SOCKETS (sheet rows 2.1, 2.2, 2.5): a stem from the wall and a
 *  cup open to the room; the schuko's bar lies on the cup's apex, and the power
 *  socket has three stems. Footed symbols are
 *  drawn centred, their foot at y = −d/2 on the wall side, so a miniature needs
 *  nothing and whoever stands one on a wall shifts it by d/2. */
function PrizaCupa({ d, bara, trei, ink }: {
  d: number; bara?: boolean; trei?: boolean; ink: string;
}) {
  const r = d / 2;
  return (
    <>
      <line x1={0} y1={-r} x2={0} y2={0} {...S(ink)} strokeWidth={LINIA_FOII} />
      {trei && [-0.32 * r, 0.32 * r].map((x) => (
        <line key={x} x1={x} y1={-r} x2={x} y2={0} {...S(ink)} strokeWidth={LINIA_FOII} />
      ))}
      <path d={`M${-r} ${r}A${r} ${r} 0 0 1 ${r} ${r}`} {...S(ink)}
            strokeWidth={LINIA_FOII} />
      {bara && <line x1={-r} y1={0} x2={r} y2={0} {...S(ink)} strokeWidth={LINIA_FOII} />}
    </>
  );
}

/** THE DATA AND TV OUTLETS (sheet rows 2.3, 2.4): a foot on the wall, a stem, and
 *  above it a frame with a U in it, or an open U with a circle. Taller than wide
 *  in the sheet's pictures, about 3 to 2. */
function PrizaCadru({ d, tv, ink }: { d: number; tv?: boolean; ink: string }) {
  const r = d / 2;
  const a = 0.325 * d;
  const u = 0.72 * a;
  const sus = (tv ? -0.02 : -0.05) * d;
  return (
    <>
      <line x1={-a} y1={-r} x2={a} y2={-r} {...S(ink)} strokeWidth={LINIA_FOII} />
      <line x1={0} y1={-r} x2={0} y2={sus} {...S(ink)} strokeWidth={LINIA_FOII} />
      {tv ? <>
        <path d={`M${-a} ${r}L${-a} ${sus}L${a} ${sus}L${a} ${r}`} {...S(ink)}
              strokeWidth={LINIA_FOII} />
        <circle cx={0} cy={0.37 * d} r={0.1 * d} {...S(ink)} strokeWidth={LINIA_FOII} />
      </> : <>
        <rect x={-a} y={sus} width={2 * a} height={r - sus} {...S(ink)}
              strokeWidth={LINIA_FOII} />
        <path d={`M${-u} ${r}L${-u} ${0.05 * d}L${u} ${0.05 * d}L${u} ${r}`}
              {...S(ink)} strokeWidth={LINIA_FOII} />
      </>}
    </>
  );
}

/** THE ARCHITECT'S SWITCHES (sheet rows 3.1, 3.2): an open circle in the room and
 *  a lever up to the wall, ending in a tick; the two-way switch has a second
 *  tick. The lever rises to the right, as on the plan mock-up. */
function ComandaPeZid({ d, doua, general, ink }: {
  d: number; doua?: boolean; ink: string;
  /** The master switch: IEC 60617 S00475's push-button, a second circle. */
  general?: boolean;
}) {
  const linie = (x1: number, y1: number, x2: number, y2: number) => (
    <line x1={x1 * d} y1={y1 * d} x2={x2 * d} y2={y2 * d} {...S(ink)}
          strokeWidth={LINIA_FOII} />
  );
  return (
    <>
      <circle cx={0} cy={0.25 * d} r={0.25 * d} {...S(ink)} strokeWidth={LINIA_FOII} />
      {linie(-0.168, 0.064, 0.14, -0.5)}
      {linie(0.14, -0.5, 0.41, -0.34)}
      {doua && linie(0.074, -0.39, 0.344, -0.23)}
      {general && <circle cx={0} cy={0.25 * d} r={0.13 * d} {...S(ink)}
                          strokeWidth={LINIA_FOII} />}
    </>
  );
}

/** CORPUL DE ILUMINAT: cercul cu crucea. Aplica e jumătatea lui, lipită de zid —
 *  și e chiar ce e o aplică: o plafonieră tăiată de perete. */
function Lumina({ d, fel, ink }: {
  d: number; fel: "plafon" | "spot" | "aplica"; ink: string;
}) {
  const r = d / 2;
  if (fel === "spot") {
    // Sheet row 4.4: two circles, a cross in the inner one, in outline.
    const k = r * 0.51;
    return (
      <g>
        <circle cx={0} cy={0} r={r} {...S(ink)} strokeWidth={LINIA_FOII} />
        <circle cx={0} cy={0} r={r * 0.72} {...S(ink)} strokeWidth={LINIA_FOII} />
        <line x1={-k} y1={-k} x2={k} y2={k} {...S(ink)} strokeWidth={LINIA_FOII} />
        <line x1={k} y1={-k} x2={-k} y2={k} {...S(ink)} strokeWidth={LINIA_FOII} />
      </g>
    );
  }
  if (fel === "aplica") {
    // Sheet row 4.3: the ceiling light's sign cut by the wall, the arc in the
    // room. Drawn footed: the diameter on y = −d/2, the arc of radius d.
    return (
      <>
        <line x1={-d} y1={-r} x2={d} y2={-r} {...S(ink)} strokeWidth={LINIA_FOII} />
        <path d={`M${-d} ${-r}A${d} ${d} 0 0 0 ${d} ${-r}`} {...S(ink)}
              strokeWidth={LINIA_FOII} />
        <line x1={0} y1={-r} x2={-d} y2={r} {...S(ink)} strokeWidth={LINIA_FOII} />
        <line x1={0} y1={-r} x2={d} y2={r} {...S(ink)} strokeWidth={LINIA_FOII} />
      </>
    );
  }
  const k = r * 0.72;
  return (
    <g>
      <circle cx={0} cy={0} r={r * 0.52} {...S(ink)} strokeWidth={LINIA_FOII} fill={FOND} />
      <line x1={-k} y1={-k} x2={k} y2={k} {...S(ink)} strokeWidth={LINIA_FOII} />
      <line x1={k} y1={-k} x2={-k} y2={k} {...S(ink)} strokeWidth={LINIA_FOII} />
    </g>
  );
}

/** TABLOUL: dreptunghiul cu jumătatea hașurată — semnul de tablou de distribuție. */
function Tablou({ d, ink }: { d: number; ink: string }) {
  const w = d;
  const h = d * 0.5;
  return (
    <g>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} {...S(ink)} fill={FOND}
            strokeWidth={g(0.028)} />
      <rect x={-w / 2} y={-h / 2} width={w / 2} height={h} fill={ink} />
    </g>
  );
}

/** THE MOUNTING BOX, empty: the white bite in the wall the architect points
 *  at (sheet row 1.1). Centred on the axis, the long side on the face (y = 0),
 *  the body into the wall — the symbol frame's −y side. */
function DozaGol({ d, ink }: { d: number; ink: string }) {
  return <rect data-doza="cutie" x={-d / 2} y={-d * 0.375} width={d}
               height={d * 0.375} {...S(ink)} fill={FOND} strokeWidth={g(0.022)} />;
}

export const INK_FAMILIE: Record<FamilieDoza, string> = {
  prize: SOCKET_INK, comenzi: SWITCH_INK,
};

/** A row's measures: each symbol's drawn size and place, the label's size and
 *  the end it stands at. One source for the drawing and for the label's box. */
function masuraRandului(kinds: readonly string[], pxPerM: number, laDreapta: boolean) {
  const ds = kinds.map((k) => drawnDiameter(marimeaSimbolului(k), pxPerM));
  const xs: number[] = [];
  let x = 0;
  ds.forEach((d, i) => {
    if (i) x += (ds[i - 1] + d) / 2 + 0.01;
    xs.push(x);
  });
  const span = xs[xs.length - 1] ?? 0;
  const fs = Math.max(...ds, 0.1) * 0.55;
  const hx = laDreapta ? span / 2 + (ds[ds.length - 1] ?? 0) / 2 + 0.04
    : -span / 2 - (ds[0] ?? 0) / 2 - 0.04;
  return { ds, xs, span, fs, hx };
}

/** One row of a box's devices: the symbols strung along the wall on the room
 *  side (sheet rows 1.2–1.4), centred on the box axis, each at its own drawn
 *  size so a floored row never overlaps itself. */
function RandDoza({ kinds, indici, y, pxPerM, ink, fam, flip, h, laDreapta }: {
  kinds: readonly string[];
  /** Each device's index, written under its symbol (sheet row 119). */
  indici: readonly string[];
  y: number; pxPerM: number; ink?: string;
  /** The box's family: the ink of its «H=», and of a symbol of no family. */
  fam: FamilieDoza;
  flip: boolean; h: number;
  /** «H=» at the row's right end, where the left one is taken. */
  laDreapta: boolean;
}) {
  const { ds, xs, span, fs, hx } = masuraRandului(kinds, pxPerM, laDreapta);
  // Footed symbols stand on one line, the row's top: different heights would
  // otherwise leave their feet at different distances from the wall.
  const sus = y - Math.max(...ds) / 2;
  return (
    <g>
      {kinds.map((k, i) => (
        <g data-doza="aparat" key={i}
           transform={`translate(${r2(xs[i] - span / 2)} ${
             r2(aparatul(k)?.picior ? sus + ds[i] / 2 : y)})`}>
          {simbolElectric(k, ds[i], ink ?? INK_FAMILIE[familiaInDoza(k) ?? fam])}
        </g>
      ))}
      {kinds.map((k, i) => {
        if (!indici[i]) return null;
        const x = r2(xs[i] - span / 2);
        const yi = r2((aparatul(k)?.picior ? sus + ds[i] / 2 : y) + ds[i] / 2 + 0.27 * ds[i]);
        return (
          <text data-doza="indice" key={`i${i}`} x={x} y={yi}
                {...(flip ? { transform: `rotate(180 ${x} ${yi})` } : {})}
                textAnchor="middle" dominantBaseline="central" fontSize={r2(0.45 * ds[i])}
                fill={ink ?? INK_FAMILIE[familiaInDoza(k) ?? fam]}
                stroke="none">{indici[i]}</text>
        );
      })}
      {/* The label grows away from the row at either end. Turned 180°, it
          anchors at its other end, or it would run back over the symbols. */}
      <text data-doza="text" x={r2(hx)} y={r2(y)}
            {...(flip ? { transform: `rotate(180 ${r2(hx)} ${r2(y)})` } : {})}
            textAnchor={flip !== laDreapta ? "start" : "end"} dominantBaseline="central"
            fontSize={r2(fs)}
            fill={ink ?? INK_FAMILIE[fam]} stroke="none">{`H=${Math.round(h * 100)}`}</text>
    </g>
  );
}

const r2 = (v: number) => Math.round(v * 1000) / 1000;

/** A glyph's device rows, as the drawing stacks them on the room side:
 *  sockets first, each row's centre line clear of the one before. One source
 *  for the glyph and for the dimension chains that must clear the rows. */
export function randurileDozei(cutii: readonly Electric[], pxPerM: number):
    { kinds: string[]; fam: FamilieDoza; h: number; idx: number; n: number;
      indici: string[]; y: number; d: number }[] {
  const randuri: { kinds: string[]; fam: FamilieDoza; h: number; idx: number;
                   n: number; indici: string[] }[] = [];
  // One row per box, its devices in the panel's order (sheet row 120): a mixed
  // box is a socket box with its switches beside its sockets.
  for (const e of cutii) {
    const kinds = e.devices ?? [];
    const f = familiaDozei(kinds);
    if (!f) continue;
    const indici = kinds.map((_, j) => e.indici?.[j] ?? "");
    randuri.push({ kinds: e.oglX ? [...kinds].reverse() : [...kinds], fam: f, h: e.h,
                   idx: e.idx, n: ramaDozei(kinds),
                   indici: e.oglX ? indici.reverse() : indici });
  }
  randuri.sort((x, y) => (x.fam === y.fam ? 0 : x.fam === "prize" ? -1 : 1));
  const ds = randuri.map((r) =>
    Math.max(...r.kinds.map((k) => drawnDiameter(marimeaSimbolului(k), pxPerM)), 0));
  let y = 0;
  return randuri.map((r, i) => {
    y += (i ? ds[i - 1] / 2 : 0) + ds[i] / 2 + 0.02;
    return { ...r, y, d: ds[i] };
  });
}

/** Where a glyph's «H=» labels stand on the plan, at one end of the rows. */
export function cutiileEtichetelorH(cutii: readonly Electric[], pxPerM: number,
                                    laDreapta: boolean):
    { x0: number; y0: number; x1: number; y1: number }[] {
  const intai = cutii[0];
  const a = ((intai.deg - 90) * Math.PI) / 180;
  const inPlan = (x: number, y: number): [number, number] =>
    [intai.c[0] + x * Math.cos(a) - y * Math.sin(a),
     intai.c[1] + x * Math.sin(a) + y * Math.cos(a)];
  return randurileDozei(cutii, pxPerM).map((r) => {
    const { fs, hx } = masuraRandului(r.kinds, pxPerM, laDreapta);
    const w = `H=${Math.round(r.h * 100)}`.length * 0.58 * fs;
    const p = inPlan(hx, r.y - fs / 2);
    const q = inPlan(hx + (laDreapta ? w : -w), r.y + fs / 2);
    return { x0: Math.min(p[0], q[0]), y0: Math.min(p[1], q[1]),
             x1: Math.max(p[0], q[0]), y1: Math.max(p[1], q[1]) };
  });
}

/** How far a glyph's rows reach into the room, for the chains that stack
 *  beyond them; an empty box has no rows and no reach. */
export function intindereaDozei(cutii: readonly Electric[], pxPerM: number): number {
  const r = randurileDozei(cutii, pxPerM);
  const ultim = r[r.length - 1];
  return ultim ? ultim.y + ultim.d / 2 : 0;
}

/** THE MOUNTING BOX with its devices — and the split rectangle when a socket
 *  box and a switch box share the spot (sheet row 1.4); one box mixing the two
 *  families is one red cell (row 120). The glyph ignores the mirror flags: the box is always in
 *  the wall and its rows in the room; `flip_x` only reverses each row. */
export function Doza({ cutii, pxPerM, alesIdx, apuca, interactiv, ink, gros,
                       hLaDreapta = false }: {
  /** One box, or the boxes coincident on one spot, sockets first. */
  cutii: readonly Electric[];
  pxPerM: number;
  alesIdx: number | null;
  apuca?: (idx: number) => (e: React.PointerEvent) => void;
  interactiv: boolean;
  ink?: string;
  /** The host wall's thickness: the drawn depth never exceeds it. */
  gros?: number;
  /** «H=» at the rows' right end (`cote::dozeleCuEtichetaLaDreapta`). */
  hLaDreapta?: boolean;
}) {
  const intai = cutii[0];
  const lung = aparatul("doza")?.d ?? 0.2;
  const s = drawnDiameter(lung, pxPerM) / lung;
  const L = lung * s;
  const adanc = Math.min(0.075 * s, gros ?? Infinity);
  // The texts turn to stay readable: their screen angle, the group's own
  // rotation, is normalised into (−90°, 90°].
  const a = (((intai.deg - 90) % 360) + 360) % 360;
  const flip = a > 90 && a <= 270;
  const randuri = randurileDozei(cutii, pxPerM);
  const spart = randuri.length >= 2;
  /** The cells of a split glyph, one per row, sockets on the left, or the
   *  whole rect: each with its box, its family ink and its module count. */
  const celule = spart
    ? randuri.map((r, i) => ({
        x: -L / 2 + (i * L) / randuri.length, w: L / randuri.length,
        fam: r.fam, idx: r.idx, n: r.n,
      }))
    : [{ x: -L / 2, w: L, fam: randuri[0]?.fam ?? null, idx: intai.idx,
         n: randuri[0]?.n ?? 0 }];
  /** One box alone is grabbed whole; of a crowd, each cell grabs its own. */
  const tinte = cutii.length > 1 ? celule
    : [{ x: -L / 2, w: L, fam: null, idx: intai.idx, n: 0 }];
  // Past two cells the width of a cell, not of the box, sets the size.
  const fs = Math.min(L * 0.42, (L / celule.length) * 0.84);
  return (
    <g data-ob="doza"
       transform={`translate(${intai.c[0]} ${intai.c[1]}) rotate(${intai.deg - 90})`}>
      {celule.map((j) => (
        <rect data-doza="cutie" key={`c${j.idx}-${j.x}`} x={r2(j.x)} y={r2(-adanc)}
              width={r2(j.w)} height={r2(adanc)}
              {...S(j.fam ? (ink ?? INK_FAMILIE[j.fam]) : (ink ?? LINIE))}
              fill={FOND} strokeWidth={g(0.022)} />
      ))}
      {celule.map((j) => j.fam && (
        <text data-doza="text" key={`t${j.idx}-${j.x}`} x={r2(j.x + j.w / 2)}
              y={r2(-adanc / 2)}
              {...(flip ? { transform:
                `rotate(180 ${r2(j.x + j.w / 2)} ${r2(-adanc / 2)})` } : {})}
              textAnchor="middle" dominantBaseline="central" fontSize={r2(fs)}
              // A cell is narrower than its label at this size: the glyphs
              // condense to stay inside it rather than run into the next one.
              {...(spart ? { textLength: r2(j.w * 0.86),
                             lengthAdjust: "spacingAndGlyphs" } : {})}
              fill={ink ?? INK_FAMILIE[j.fam]}
              stroke="none">{`${j.n}M`}</text>
      ))}
      {randuri.map((r, i) => (
        <RandDoza key={i} kinds={r.kinds} indici={r.indici} y={r.y} pxPerM={pxPerM}
                  ink={ink} fam={r.fam} flip={flip} h={r.h}
                  laDreapta={hLaDreapta} />
      ))}
      {tinte.map((j) => alesIdx === j.idx && (
        <rect data-doza="sel" key={`s${j.idx}-${j.x}`} x={r2(j.x)}
              y={r2(-adanc)} width={r2(j.w)}
              height={r2(adanc)} fill="none" stroke="#1668c4" strokeWidth={2.5}
              vectorEffect="non-scaling-stroke" />
      ))}
      {interactiv && tinte.map((j) => (
        <rect data-doza="tinta" key={`g${j.idx}-${j.x}`} x={j.x}
              y={-adanc} width={j.w} height={adanc} fill="transparent"
              strokeWidth={0} style={{ cursor: "move" }}
              pointerEvents="all" onPointerDown={apuca?.(j.idx)} />
      ))}
    </g>
  );
}

/** COINCIDENT BOXES, grouped for the split glyph (sheet row 1.4): boxes with
 *  devices on one spot read as one rectangle, sockets first, because drawn
 *  apart they would print over each other. Empty boxes are never grouped. */
export function grupeleDozelor(aparate: readonly Electric[]): Electric[][] {
  const doze = aparate.filter((a) => a.kind === "doza");
  // The hair above the written tolerance: coordinates are stored to the
  // millimetre, and a double subtraction of two such values may land just
  // over it — a nudged pair at exactly 20 mm must still group.
  const FIR = 1e-9;
  const coincid = (a: Electric, b: Electric): boolean => {
    const dd = (((b.deg - a.deg) % 360) + 360) % 360;
    if (Math.min(dd, 360 - dd) > 6) return false;
    const rad = (a.deg * Math.PI) / 180;
    const dx = b.c[0] - a.c[0], dy = b.c[1] - a.c[1];
    return Math.abs(dx * Math.cos(rad) + dy * Math.sin(rad)) <= 0.05 + FIR
      && Math.abs(-dx * Math.sin(rad) + dy * Math.cos(rad)) <= 0.02 + FIR;
  };
  const ordinea = (e: Electric) => {
    const f = familiileDozei(e.devices ?? []);
    return f.length !== 1 ? 1 : f[0] === "prize" ? 0 : 2;
  };
  const luat = new Set<number>();
  const out: Electric[][] = [];
  for (const a of doze) {
    if (luat.has(a.idx)) continue;
    luat.add(a.idx);
    const multi = (a.devices ?? []).length
      ? doze.filter((b) => !luat.has(b.idx) && (b.devices ?? []).length
                           && coincid(a, b)) : [];
    for (const b of multi) luat.add(b.idx);
    out.push([a, ...multi].sort((x, y) => ordinea(x) - ordinea(y)));
  }
  return out;
}

/** The ink of a kind, by the GROUP the catalogue puts it in, not by the
 *  device's circuit: the request is about what the symbol is, and a document
 *  may route a socket on another circuit while the symbol must still read as a
 *  socket. Sockets take the architect's red, switches and lights their blue,
 *  as the office draws them; the wires keep the circuit colours. The board
 *  and anything unknown keep the plan's black. */
function symbolInk(kind: string): string {
  const grupa = aparatul(kind)?.grupa;
  if (grupa === "Prize") return SOCKET_INK;
  if (grupa === "Comenzi" || grupa === "Iluminat") return SWITCH_INK;
  return LINIE;
}

/** Simbolul unui fel. `null` pentru unul necunoscut — un document scris de o
 *  versiune mai nouă nu strică desenul, doar nu arată aparatul ăla.
 *  `ink` replaces the kind's colour where a reader filters pale inks out, as
 *  the DWG export does. */
export function simbolElectric(kind: string, d: number,
                               ink: string = symbolInk(kind)): ReactNode {
  switch (kind) {
    case "priza":
      return <g data-simbol={kind}><PrizaCupa d={d} bara ink={ink} /></g>;
    case "priza_simpla":
      return <g data-simbol={kind}><PrizaCupa d={d} ink={ink} /></g>;
    case "priza_date":
      return <g data-simbol={kind}><PrizaCadru d={d} ink={ink} /></g>;
    case "priza_dubla": return <Priza d={d} n={2} ink={ink} />;
    case "priza_blat": return <Priza d={d} ink={ink} />;
    case "priza_forta":
      return <g data-simbol={kind}><PrizaCupa d={d} bara trei ink={ink} /></g>;
    case "priza_tv":
      return <g data-simbol={kind}><PrizaCadru d={d} tv ink={ink} /></g>;
    case "intrerupator":
      return <g data-simbol={kind}><ComandaPeZid d={d} ink={ink} /></g>;
    case "intrerupator_dublu": return <Comanda d={d} ink={ink} />;
    case "intrerupator_general":
      return <g data-simbol={kind}><ComandaPeZid d={d} general ink={ink} /></g>;
    case "comutator":
      return <g data-simbol={kind}><ComandaPeZid d={d} doua ink={ink} /></g>;
    case "plafoniera": return <Lumina d={d} fel="plafon" ink={ink} />;
    case "spot": return <Lumina d={d} fel="spot" ink={ink} />;
    case "aplica":
      return <g data-simbol={kind}><Lumina d={d} fel="aplica" ink={ink} /></g>;
    case "tablou": return <Tablou d={d} ink={ink} />;
    // A palette's or a ghost's strip: a short dashed run along the wall.
    case "banda_led":
      return <line data-simbol={kind} x1={-0.15} y1={0} x2={0.15} y2={0} {...S(ink)}
                   strokeWidth={2 * LINIA_FOII} strokeDasharray="8 5" />;
    // The ghost and the palette miniature show the empty bite; the placed box
    // is drawn whole, devices and all, by `Doza` from `Aparate`.
    case "doza": return <DozaGol d={d} ink={ink} />;
    default: return null;
  }
}

/** SUB CÂTE PIXELI NU SE MAI DESENEAZĂ UN APARAT.
 *
 *  O priză are 16 cm. Încadrat pe tot modulul, simbolul ei are doi-trei pixeli
 *  și devine o pată — iar patruzeci de pete una lângă alta se citesc ca murdărie
 *  pe planșă, nu ca instalație. Traseele rămân desenate: ele au ce spune și de
 *  departe, fiindcă arată pe unde trec firele prin pereți. */
/** SIMBOLURILE ELECTRICE NU SUNT LA SCARĂ, și nici pe planșe adevărate nu sunt.
 *
 *  Aici era un PRAG: un aparat al cărui simbol ieșea sub 11 px pe ecran nu se
 *  desena deloc. Socoteala: o priză are `d = 0,16 m`, deci îi trebuiau 69 px/m
 *  ca să apară — iar un tronson întreg se citește la 33–37, și mărirea maximă e
 *  90. Adică instalația electrică era INVIZIBILĂ la orice mărire la care
 *  lucrează cineva: puneai o priză, nu se întâmpla nimic pe ecran, și nu aveai
 *  de unde să știi dacă s-a scris sau nu. La fel traseele, care se desenează din
 *  aparate: fără aparate văzute, nici firele n-aveau ce arăta.
 *
 *  Pragul era răspunsul greșit la o întrebare bună — un simbol de 6 px chiar nu
 *  se citește. Răspunsul bun e cel de pe planșe: simbolul se desenează la o
 *  mărime a LUI, nu la scara zidului. Deci diametrul desenat are un PLANȘEU în
 *  pixeli, iar sub el simbolul se lățește în loc să dispară. */
const PX_MINIM_APARAT = 13;

/** The diameter a symbol is drawn at, in metres: the floor above applied
 *  once, here, so the placed symbol and the ghost that announces it agree. */
export function drawnDiameter(d: number, pxPerM: number): number {
  return Math.max(d, PX_MINIM_APARAT / pxPerM);
}

/** Sub atâția pixeli pe metru nu se mai desenează nici planul, darămite
 *  instalația: la 8 px/m un modul de 10 m are 80 px cu totul. */
export const PRAG_APARAT = 8;

/** TRASEELE, ca polilinii pe axele pereților.
 *
 *  DECALATE PE CIRCUIT, cu câțiva centimetri. Firele de lumină și cele de prize
 *  merg prin aceiași pereți, deci desenate pe același ax s-ar suprapune exact și
 *  s-ar vedea doar ultimul. Decalajul e mai mic decât grosimea unui zid, deci
 *  traseul rămâne în peretele lui — nu e o minciună de desen, e o desfacere.
 *
 *  Linia e ÎNTRERUPTĂ, fiindcă firul e îngropat: convenția de planșă pentru ce
 *  trece prin perete și nu se vede. */
const DECALAJ: Record<Circuit, number> = {
  lumina: 0.035, prize: -0.035, forta: -0.08, date: 0.08,
};

/** Polilinia decalată perpendicular pe ea însăși, cu `d`. Pe colțuri se
 *  decalează fiecare segment și se leagă capetele — destul pentru trei
 *  centimetri, și fără socoteala de intersecții a unui offset adevărat. */
function decalata(drum: readonly Pt[], d: number): string {
  if (drum.length < 2) return "";
  const out: string[] = [];
  for (let i = 0; i + 1 < drum.length; i++) {
    const [x0, y0] = drum[i];
    const [x1, y1] = drum[i + 1];
    const L = Math.hypot(x1 - x0, y1 - y0) || 1;
    const nx = (-(y1 - y0) / L) * d;
    const ny = ((x1 - x0) / L) * d;
    out.push(`${i ? "L" : "M"}${x0 + nx} ${y0 + ny}L${x1 + nx} ${y1 + ny}`);
  }
  return out.join("");
}

export function Trasee({ trasee, aparate, alesIdx }: {
  trasee: readonly Traseu[];
  aparate: readonly Electric[];
  /** Traseul aparatului ales se aprinde; restul se sting. Cu patruzeci de
   *  aparate, „toate firele deodată" e un ghem — iar întrebarea pe care și-o
   *  pune cineva e aproape întotdeauna despre UN aparat. */
  alesIdx: number | null;
}) {
  const pePozitie = new Map(aparate.map((a) => [a.idx, a]));
  return (
    <g pointerEvents="none">
      {trasee.map((t) => {
        const c = CIRCUITE[t.circuit];
        const aprins = alesIdx == null || alesIdx === t.idx;
        const a = pePozitie.get(t.idx);
        return (
          // The circuit joins the key: a mounting box has one route per
          // circuit, all with the box's idx.
          <g key={`${t.idx}-${t.circuit}`} opacity={aprins ? 1 : 0.16}>
            <path d={decalata(t.drum, DECALAJ[t.circuit])} fill="none"
                  stroke={c.culoare} strokeWidth={aprins && alesIdx != null ? 2.4 : 1.5}
                  strokeDasharray="7 4" strokeLinecap="round"
                  vectorEffect="non-scaling-stroke" />
            {/* RACORDUL: de la capătul traseului la aparat. Firul iese din banda
                de pozare și coboară (sau urcă) pe verticală — în plan, verticala
                aia e un punct, deci se desenează segmentul care rămâne: bucata
                orizontală de la ax până sub aparat. */}
            {a && t.drum.length > 0 && (
              <line x1={t.drum[t.drum.length - 1][0]} y1={t.drum[t.drum.length - 1][1]}
                    x2={t.la[0]} y2={t.la[1]} stroke={c.culoare} strokeWidth={1.2}
                    strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
            )}
          </g>
        );
      })}
    </g>
  );
}

/** A device's grab radius: the symbol, but never under a fingertip's 9 px. */
const razaPrinsei = (dDesen: number, pxPerM: number) =>
  Math.max(dDesen * 0.8, 9 / pxPerM);

/** Where a single device takes a click, in plan coordinates: the grab circle
 *  `Aparate` draws. A footed symbol's centre stands half its drawn size into
 *  the room, along the wall's normal. Boxes have their own grab area. */
export function prinsaAparatului(a: Electric, pxPerM: number):
  { c: Pt; r: number } | null {
  if (a.kind === "doza" || pxPerM < PRAG_APARAT) return null;
  const fel = aparatul(a.kind);
  const dDesen = drawnDiameter(fel ? marimeaSimbolului(a.kind) : 0.18, pxPerM);
  const r = razaPrinsei(dDesen, pxPerM);
  if (!fel?.picior) return { c: a.c, r };
  const u = (a.deg * Math.PI) / 180;
  return { c: [a.c[0] + Math.cos(u) * dDesen / 2,
               a.c[1] + Math.sin(u) * dDesen / 2], r };
}

/** THE LED STRIP (sheet row 114): a thick dashed line 2 cm off its face, from
 *  its start to its end, its «H=» past the start and its index past the «H=».
 *  Drawn in the face's frame: local x runs along the strip, local y into the
 *  room. */
function BandaLed({ a, pxPerM, ales, apuca, interactiv, ink }: {
  a: Electric; pxPerM: number; ales: boolean; interactiv: boolean; ink?: string;
  apuca?: (idx: number) => (e: React.PointerEvent) => void;
}) {
  const rad = (a.deg * Math.PI) / 180;
  const end = a.end ?? a.c;
  const L = r2((end[0] - a.c[0]) * Math.sin(rad) - (end[1] - a.c[1]) * Math.cos(rad));
  const y = 0.02;
  const semn = L >= 0 ? 1 : -1;
  const cerneala = ink ?? symbolInk(a.kind);
  const unghi = (((a.deg - 90) % 360) + 360) % 360;
  const intors = unghi > 90 && unghi <= 270;
  // Text grows away from the strip; turned upright, from its other end.
  const ancora = (semn > 0) !== intors ? "end" : "start";
  const fs = r2(drawnDiameter(0.1, pxPerM) * 0.55);
  const hText = `H=${Math.round(a.h * 100)}`;
  const xh = r2(-semn * 0.04);
  const xi = r2(xh - semn * (hText.length * 0.58 * fs + 0.04));
  const linie = { x1: 0, y1: y, x2: L, y2: y, vectorEffect: "non-scaling-stroke" as const };
  const text = (x: number, s: string, cheie: string) => (
    <text data-banda={cheie} x={x} y={y}
          {...(intors ? { transform: `rotate(180 ${x} ${y})` } : {})}
          textAnchor={ancora} dominantBaseline="central" fontSize={fs}
          fill={cerneala} stroke="none">{s}</text>
  );
  return (
    <g transform={`translate(${a.c[0]} ${a.c[1]}) rotate(${a.deg - 90})`}>
      {ales && <line data-banda="ales" {...linie} stroke="#1668c4"
                      strokeWidth={2 * LINIA_FOII + 4} />}
      <line data-banda="linie" {...linie} stroke={cerneala}
            strokeWidth={2 * LINIA_FOII} strokeDasharray="8 5" />
      {text(xh, hText, "h")}
      {a.index && text(xi, a.index, "indice")}
      <line data-banda="tinta" {...linie} stroke="transparent" strokeWidth={9}
            style={{ cursor: interactiv ? "move" : undefined }}
            pointerEvents={interactiv ? "stroke" : "none"}
            onPointerDown={apuca?.(a.idx)} />
    </g>
  );
}

/** APARATELE, fiecare rotit spre camera lui. */
export function Aparate({ aparate, pxPerM, alesIdx, apuca, interactiv, ink,
                          grosimi, hDreapta }: {
  aparate: readonly Electric[];
  pxPerM: number;
  alesIdx: number | null;
  apuca?: (idx: number) => (e: React.PointerEvent) => void;
  interactiv: boolean;
  /** One ink for every symbol instead of the kind's colour: the DWG export
   *  keeps the plan's black, because its primitive reader drops pale inks. */
  ink?: string;
  /** Host-wall thickness per mounting box (`grosimileDozelor`): the drawn
   *  depth cap. Without it a floored box may overdraw a thin wall. */
  grosimi?: ReadonlyMap<number, number>;
  /** Glyphs whose «H=» stands at the rows' right end, by first box. */
  hDreapta?: ReadonlySet<number>;
}) {
  return (
    <g>
      {aparate.map((a) => {
        // Boxes are drawn below, grouped: two on one spot share a split glyph.
        if (a.kind === "doza") return null;
        if (a.kind === "banda_led") {
          return pxPerM < PRAG_APARAT ? null
            : <BandaLed key={a.idx} a={a} pxPerM={pxPerM} ales={alesIdx === a.idx}
                        apuca={apuca} interactiv={interactiv} ink={ink} />;
        }
        const fel = aparatul(a.kind);
        const d = fel ? marimeaSimbolului(a.kind) : 0.18;
        if (pxPerM < PRAG_APARAT) return null;
        // Diametrul DESENAT: al aparatului, dar nu mai mic de câțiva pixeli pe
        // ecran — v. `PX_MINIM_APARAT`.
        const dDesen = drawnDiameter(d, pxPerM);
        const sim = simbolElectric(a.kind, dDesen, ink);
        if (!sim) return null;
        const sel = alesIdx === a.idx;
        // A footed symbol stands in the room whatever its flags say: `flip_y`
        // would put it in the wall. Shifted here, ring and grab area follow.
        const oglY = a.oglY && !fel?.picior;
        return [
          // Oglindirea vine DUPĂ rotire, ca la mobilier: altfel simbolul s-ar
          // răsturna în jurul axelor modulului, nu ale lui, și un aparat pus pe
          // un perete oblic ar pleca de pe el. Neatinsă, nu scrie nimic în
          // markup — un `scale(1 1)` pe fiecare din cele patruzeci de aparate
          // ale unui modul e cost de desen fără niciun efect.
          <g key={a.idx}
             transform={`translate(${a.c[0]} ${a.c[1]}) rotate(${a.deg - 90})`
               + (a.oglX || oglY
                 ? ` scale(${a.oglX ? -1 : 1} ${oglY ? -1 : 1})` : "")
               + (fel?.picior ? ` translate(0 ${r2(dDesen / 2)})` : "")}>
            {sel && (
              <circle cx={0} cy={0} r={dDesen * 0.9} fill="none" stroke="#1668c4"
                      strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
            )}
            {sim}
            {/* Suprafața de apucat: simbolurile sunt din linii subțiri, iar un
                click pe golul dintre ele n-ar prinde nimic. */}
            {/* `strokeWidth={0}` din același motiv ca la goluri (v. `Desen`):
                cu `pointerEvents="all"` răspunde și conturul, iar o linie
                nedeclarată e groasă de o unitate de desen — un metru. */}
            <circle cx={0} cy={0} r={razaPrinsei(dDesen, pxPerM)}
                    fill="transparent" strokeWidth={0}
                    style={{ cursor: interactiv ? "move" : undefined }}
                    pointerEvents={interactiv ? "all" : "none"}
                    onPointerDown={apuca?.(a.idx)} />
          </g>,
          // The device's texts, outside the mirror so they never read
          // backwards: the wall light's «H=» past its right end (sheet row
          // 4.3), the index past its left end (row 119). A ceiling light's
          // index stands upright.
          (a.kind === "aplica" || a.index) && (() => {
            const peZid = ePePerete(a.kind);
            const unghi = (((a.deg - 90) % 360) + 360) % 360;
            const intors = peZid && unghi > 90 && unghi <= 270;
            const xh = r2(dDesen + 0.04);
            const xi = r2(-((a.kind === "aplica" ? 2 * dDesen : dDesen) / 2 + 0.04));
            const cerneala = ink ?? symbolInk(a.kind);
            return (
              <g key={`t${a.idx}`} pointerEvents="none"
                 transform={peZid
                   ? `translate(${a.c[0]} ${a.c[1]}) rotate(${a.deg - 90})`
                     + (fel?.picior ? ` translate(0 ${r2(dDesen / 2)})` : "")
                   : `translate(${a.c[0]} ${a.c[1]})`}>
                {a.kind === "aplica" && (
                  <text data-aparat="h" x={xh} y={0}
                        {...(intors ? { transform: `rotate(180 ${xh} 0)` } : {})}
                        textAnchor={intors ? "end" : "start"} dominantBaseline="central"
                        fontSize={r2(dDesen * 0.55)} fill={cerneala}
                        stroke="none">{`H=${Math.round(a.h * 100)}`}</text>
                )}
                {a.index && (
                  <text data-aparat="indice" x={xi} y={0}
                        {...(intors ? { transform: `rotate(180 ${xi} 0)` } : {})}
                        textAnchor={intors ? "start" : "end"} dominantBaseline="central"
                        fontSize={r2(dDesen * 0.45)} fill={cerneala}
                        stroke="none">{a.index}</text>
                )}
              </g>
            );
          })(),
        ];
      })}
      {pxPerM >= PRAG_APARAT && grupeleDozelor(aparate).map((u) => (
        <Doza key={u[0].idx} cutii={u} pxPerM={pxPerM} alesIdx={alesIdx}
              apuca={apuca} interactiv={interactiv} ink={ink}
              gros={grosimi?.get(u[0].idx)}
              hLaDreapta={hDreapta?.has(u[0].idx)} />
      ))}
    </g>
  );
}
